import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { toast } from "react-toastify";
import { Box } from "@mui/material";

import { PageLayout } from "../../layouts/PageLayout";
import { CardLayout, Invoice, PageHeader } from "../../components/v3";
import { DownloadAndPrint } from "../../components/DownloadAndPrint";
import { get_date } from "../../components/generalComponets";
import { AccountService, FinanceService } from "../../services";
import { getAccountName, getDateAndTime } from "../../utils/formatters";

interface UserInfo {
  firstName: string;
  lastName: string;
}

interface WorkerReceiptData {
  id?: string;
  date?: string;
  project?: { name: string };
  description?: string;
  amount?: string | number;
  createdBy?: UserInfo;
  createdAt?: string;
  updatedBy?: UserInfo;
  updatedAt?: string;
  toAccountName?: string;
  fromAccountName?: string;
  fromAccountId?: string;
  toAccountId?: string;
}

export const WorkerReceipt = () => {
  const [invoiceData, setInvoiceData] = useState<Partial<WorkerReceiptData>>({});

  const { paymentId } = useParams();
  const printRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchData(paymentId);
  }, [paymentId]);

  const fetchData = async (invoiceId) => {
    try {
      const { data: response } = await FinanceService.getTransactionById(invoiceId);
      let invoicePayload = { ...response };

      const accountIds = Array.from(new Set([response.fromAccountId, response.toAccountId].filter(Boolean)));
      if (accountIds.length > 0) {
        const { data: accounts } = await AccountService.getAccounts({
          ids: accountIds.join(","),
          limit: accountIds.length,
          includeDeleted: true,
          includeOwnerDetails: true,
          includeRestrictedTypes: true,
        });

        const fromAccount = accounts.rows.find((account) => account.id === response.fromAccountId);
        const toAccount = accounts.rows.find((account) => account.id === response.toAccountId);

        invoicePayload = {
          ...invoicePayload,
          fromAccountName: getAccountName(fromAccount?.type, fromAccount),
          toAccountName: getAccountName(toAccount?.type, toAccount),
        };
      }

      setInvoiceData(invoicePayload);
    } catch (error) {
      toast.error("Failed to fetch payslip data");
      console.error("🚀 ~ fetchData ~ error:", error);
    }
  };

  const handlePrint = useReactToPrint({
    contentRef: printRef,
  });

  const rows = [
    {
      invoiceNo: invoiceData?.id,
      date: get_date(invoiceData?.date),
      project: invoiceData?.project?.name || invoiceData?.description || "N/A",
      amount: invoiceData?.amount,
    },
  ];

  return (
    <>
      <PageLayout>
        {/* header section */}
        <Box sx={{ position: "relative", zIndex: 1 }}>
          <PageHeader title="Payslip" />
        </Box>
        <Box sx={{ mt: -9, zIndex: 2, position: "relative" }}>
          <CardLayout>
            <div ref={printRef}>
              <Invoice
                title="Payslip"
                sectionB={[
                  {
                    label: "Created By",
                    value: invoiceData?.createdBy
                      ? `${invoiceData?.createdBy?.firstName} ${invoiceData?.createdBy?.lastName} (${getDateAndTime(
                          invoiceData?.createdAt,
                        )})`
                      : "N/A",
                  },
                  invoiceData?.updatedBy && {
                    label: "Last Updated By",
                    value: `${invoiceData?.updatedBy?.firstName ?? ""} ${
                      invoiceData?.updatedBy?.lastName ?? ""
                    } (${getDateAndTime(invoiceData?.updatedAt)})`,
                  },
                ].filter(Boolean)}
                sectionC={[{ label: "Payment To", value: invoiceData?.toAccountName || "-" }]}
                sectionD={[{ label: "Payment From", value: invoiceData?.fromAccountName || "-" }]}
                columns={[
                  { id: "invoiceNo", label: "Invoice No" },
                  { id: "date", label: "Date" },
                  { id: "project", label: "Project/Remarks" },
                  { id: "amount", label: "Amount" },
                ]}
                rows={rows}
                subtotal={invoiceData?.amount}
              />
            </div>
          </CardLayout>
        </Box>

        <DownloadAndPrint handlePrint={handlePrint} printRef={printRef} fileName={"WorkerReceipt.pdf"} />
      </PageLayout>
    </>
  );
};
